import { Suspense } from 'react'
import { Helmet } from 'react-helmet-async'
import { useIntl } from 'react-intl'
import { View } from 'react-native'
import styled from 'styled-components'
import { lazy } from '@loadable/component'
import {
  useFeatureFlag,
  useFilmGroupListQuery,
  useFilmGroupListsQuery, // useLiveEventsQuery,
  useUserWatchListSlugs,
} from '@mwxltd/flix-api-client'
import { useNestedListAdInsertion } from '@flix/common/domain/ads/listAdInsertion/useNestedListAdInsertion'
import { HorizontalLists } from '@flix/common/domain/content-lists-horizontal/HorizontalLists'
import { PosterZoomProvider } from '@flix/common/domain/content-lists-horizontal/PosterZoomProvider/PosterZoomProvider'
import { useButtonHandlers } from '@flix/common/elements/controls'
import { ComponentError, ComponentPlaceholder } from '@flix/common/elements/status'
import { PaymentModelProvider } from '@flix/common/hooks/usePaymentModel/PaymentModelProvider'
import { canUseDOM } from '@flix/common/utils/canUseDOM'
import config from '../../../config'
import errorMsgs from '../../../containers/errors/messages'
import { useWasBackNavigation } from '../../../containers/useWasBackNavigation/useWasBackNavigation'
import { canonicalUrl } from '../../../utils/htmlHead'
import meta from '../../metaDescriptions'
import { NowShowingLeaderboardAd } from './NowShowingLeaderboardAd'

// Lazy load to postpone needing react-native-reanimated and the video player
const CarouselZoomedPanel = lazy(() =>
  import('../../../containers/filmCarouselZoom/CarouselZoomedPanel'),
)

const ListsContainer = styled(View)`
  margin-bottom: ${(props) => props.theme.spacing.xxxlarge}px;
  min-height: 1080px;
`

const NowShowingPage = ({ location }) => {
  const { getFlagValue } = useFeatureFlag()
  const bannerAdsEnabled = getFlagValue('bannerAdsEnabled')
  const channelsEnabled = getFlagValue('channelsEnabled')

  const intl = useIntl()
  const wasBackNav = useWasBackNavigation()
  const {
    handlers: { viewDetails },
  } = useButtonHandlers()

  const { loading, error, data } = useFilmGroupListsQuery({
    groupSlug: 'now-showing',
    trackView: !wasBackNav,
  })

  const enabledChannelsList = channelsEnabled ? 'promo' : null

  const { data: streamingNow } = useFilmGroupListQuery({
    groupSlug: enabledChannelsList,
    listSlug: 'streaming-now',
  })
  const { data: catchUp } = useFilmGroupListQuery({
    groupSlug: enabledChannelsList,
    listSlug: 'catch-up',
  })
  const { data: topChannels } = useFilmGroupListQuery({
    groupSlug: enabledChannelsList,
    listSlug: 'top-channels',
  })

  const watchlist = useUserWatchListSlugs()

  const list = useNestedListAdInsertion({
    list: data?.filmGroupLists,
    skip: !bannerAdsEnabled,
  })

  // Temporary hook returns an array of mock live events.
  // const liveEvents = useLiveEventsQuery()

  if (loading) {
    return (
      <ListsContainer>
        <ComponentPlaceholder activityIndicator />
      </ListsContainer>
    )
  }
  if (error) {
    return (
      <ListsContainer>
        <ComponentError {...errorMsgs.loadingFilms} />
      </ListsContainer>
    )
  }

  const listWithLiveEvents = [
    ...list.items.slice(0, 2),
    // Live Events
    /* {
      __typename: 'FilmGroupList',
      slug: 'liveEvents',
      title: 'Live and Upcoming Events',
      listType: 'featured-portrait',
      items: liveEvents,
    }, */
    // Top Streaming Now
    streamingNow?.filmGroupList,
    ...list.items.slice(2, 4),
    // CatchUp
    catchUp?.filmGroupList,
    // Top Channels
    topChannels?.filmGroupList,
    ...list.items.slice(4),
  ]

  let nowShowingLists = list.items
  if (!!enabledChannelsList) {
    nowShowingLists = listWithLiveEvents
  }

  return (
    <>
      <Helmet>
        <title>{intl.formatMessage(meta.nowShowingTitle)}</title>
        <meta property="og:title" content="Now Showing on Flix Premiere" />
        <meta
          name="description"
          property="og:description"
          content={intl.formatMessage(meta.nowShowing)}
        />
        <meta property="og:url" content={canonicalUrl(location)} />
        <link rel="canonical" href={canonicalUrl(location)} />
      </Helmet>

      <ListsContainer>
        <PaymentModelProvider>
          <PosterZoomProvider
            renderZoomedPanel={([activeItem, clearActiveItem]) => (
              <Suspense fallback={<View />}>
                <CarouselZoomedPanel
                  {...activeItem}
                  onItemChosen={viewDetails}
                  onMouseLeave={clearActiveItem}
                  isOnWatchList={watchlist?.[activeItem.item.slug]}
                />
              </Suspense>
            )}
            portalNode={
              canUseDOM ? document?.getElementById(config.reactPortalAnchorId) : undefined
            }
          >
            <HorizontalLists
              items={nowShowingLists}
              itemProps={{
                watchlist,
                portalAnchorId: config.reactPortalAnchorId,
                premiumTitle: true,
              }}
              onItemChosen={viewDetails}
              landscapePosters
            />
          </PosterZoomProvider>
        </PaymentModelProvider>
      </ListsContainer>

      {bannerAdsEnabled && (
        <NowShowingLeaderboardAd
          zone="nowShowing"
          placementSm="belowListLeaderboard"
          placementXs="belowListLeaderboardMobile"
        />
      )}
    </>
  )
}

export default NowShowingPage
